import { Check, ImageOff, Info, Minus, Plus, RotateCcw, Scan, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react'

import { Button } from '@krp/ui-kit'

import '../../../shared/platform.css'
import {
  CROP_HANDLES,
  HANDLE_LABELS,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
  boxHeight,
  boxWidth,
  clampPan,
  fitBox,
  fitOf,
  imagePixelsPerScreenPixel,
  moveBox,
  parseHandle,
  resizeBox,
  resultSide,
  sameBox,
  willShrink,
  zoomAround,
  type CropFrame,
  type CropHandle,
  type Pan,
} from '../model/photoCrop'
import type { CropBox, PhotoCropReviewVm } from '../model/types'
import './photoCrop.css'

type Gesture =
  | { kind: 'pan'; id: number; startX: number; startY: number; pan: Pan }
  | { kind: 'move'; id: number; startX: number; startY: number; box: CropBox }
  | { kind: 'resize'; id: number; handle: CropHandle; startX: number; startY: number; box: CropBox }
  | { kind: 'pinch'; distance: number; zoom: number; pan: Pan }

interface View {
  zoom: number
  pan: Pan
}

const HOME: View = { zoom: ZOOM_MIN, pan: { x: 0, y: 0 } }

/** Где ручка стоит на рамке (доли рамки). */
const HANDLE_AT: Readonly<Record<CropHandle, { left: string; top: string }>> = {
  nw: { left: '0%', top: '0%' },
  ne: { left: '100%', top: '0%' },
  sw: { left: '0%', top: '100%' },
  se: { left: '100%', top: '100%' },
  n: { left: '50%', top: '0%' },
  s: { left: '50%', top: '100%' },
  w: { left: '0%', top: '50%' },
  e: { left: '100%', top: '50%' },
}

/** Стороны рисуются первыми, углы — поверх них: на мелкой рамке угол остаётся доступным. */
const HANDLE_ORDER: readonly CropHandle[] = [...CROP_HANDLES].reverse()

const ARROWS: Readonly<Record<string, readonly [number, number]>> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
}

const percent = (value: number, total: number): string => `${String((value / total) * 100)}%`

/** Захват указателя необязателен: без него жест работает, пока палец над окном просмотра. */
function capture(element: HTMLElement, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId)
  } catch {
    // Среда без захвата указателя — жест всё равно идёт по событиям окна просмотра.
  }
}

/**
 * Проверка обрезки при загрузке фото (RP-FB73): вырез БЕЗ фона и поверх него пунктирная рамка — где пройдёт обрезка.
 *
 * - Рамка всегда редактируема: потяните за угол или сторону, чтобы изменить размер, за середину — чтобы передвинуть. «Принять» берёт ту рамку,
 *   что на экране; нажатие без правок принимает предложенную. Ручки с зоной касания 44 px; клавиши-стрелки двигают границу (Shift — на 10 px).
 * - Для точной подгонки вырез увеличивается и сдвигается: кнопки «Увеличить» / «Уменьшить» / «По размеру» (они всегда есть), щипок двумя пальцами и
 *   колесо мыши. Жест не единственный способ.
 * - Подпись показывает размер предмета в рамке и размер итогового квадрата; предмет не растягивается — вокруг добавляются отступ и белое поле.
 * - «Отмена» — не загружать: на сервере ничего не сохранено.
 */
export function PhotoCropReview({
  review,
  onAccept,
  onCancel,
  busy = false,
  busyText = null,
  startBox,
  acceptKind = 'save',
}: {
  review: PhotoCropReviewVm
  onAccept: (box: CropBox) => void
  onCancel: () => void
  /** Идёт сохранение: рамка заблокирована, «Принять» занято. */
  busy?: boolean
  /** Слова этапа сохранения («Отправляю на сервер»…), пока `busy`. */
  busyText?: string | null
  /** Рамка, с которой начать правку (например, возврат от предпросмотра); без неё — предложенная сервером. */
  startBox?: CropBox
  /** Что делает главная кнопка: `save` — «Принять» сохраняет фото; `show-result` — в «Добавить фото» она только показывает результат. */
  acceptKind?: 'save' | 'show-result'
}) {
  const frame = useMemo<CropFrame>(() => ({ width: review.width, height: review.height }), [review.width, review.height])
  const proposed = useMemo(() => fitBox(review.proposedBox, frame), [review.proposedBox, frame])
  const [box, setBox] = useState<CropBox>(startBox === undefined ? proposed : fitBox(startBox, frame))
  const [view, setView] = useState<View>(HOME)
  const [broken, setBroken] = useState(false)
  const stage = useRef<HTMLDivElement>(null)
  const gesture = useRef<Gesture | null>(null)
  const pointers = useRef(new Map<number, { x: number; y: number }>())

  const zoomBy = useCallback(
    (factor: number) => setView((current) => zoomAround(current.pan, current.zoom, current.zoom * factor, frame)),
    [frame],
  )

  // Колесо мыши увеличивает вырез, а не листает окно: слушатель должен быть не пассивным, поэтому — не через React.
  useEffect(() => {
    const element = stage.current
    if (element === null) return undefined
    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      zoomBy(event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP)
    }
    element.addEventListener('wheel', onWheel, { passive: false })
    return () => element.removeEventListener('wheel', onWheel)
  }, [zoomBy])

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (busy) return
    capture(event.currentTarget, event.pointerId)
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (pointers.current.size >= 2) {
      const [first, second] = [...pointers.current.values()]
      if (first === undefined || second === undefined) return
      gesture.current = { kind: 'pinch', distance: Math.hypot(first.x - second.x, first.y - second.y), zoom: view.zoom, pan: view.pan }
      return
    }
    const target = event.target instanceof Element ? event.target : null
    const handle = parseHandle(target?.closest<HTMLElement>('[data-handle]')?.dataset.handle)
    const start = { id: event.pointerId, startX: event.clientX, startY: event.clientY }
    if (handle !== null) gesture.current = { kind: 'resize', handle, box, ...start }
    else if ((target?.closest('[data-role="box"]') ?? null) !== null) gesture.current = { kind: 'move', box, ...start }
    else gesture.current = { kind: 'pan', pan: view.pan, ...start }
  }

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const known = pointers.current.get(event.pointerId)
    if (known === undefined) return
    known.x = event.clientX
    known.y = event.clientY
    const current = gesture.current
    if (current === null) return
    if (current.kind === 'pinch') {
      const [first, second] = [...pointers.current.values()]
      if (first === undefined || second === undefined || current.distance <= 0) return
      const distance = Math.hypot(first.x - second.x, first.y - second.y)
      setView(zoomAround(current.pan, current.zoom, (current.zoom * distance) / current.distance, frame))
      return
    }
    const size = stage.current?.getBoundingClientRect().width ?? 0
    if (current.id !== event.pointerId || size <= 0) return
    const dx = event.clientX - current.startX
    const dy = event.clientY - current.startY
    if (current.kind === 'pan') {
      setView((state) => ({ zoom: state.zoom, pan: clampPan({ x: current.pan.x + dx / size, y: current.pan.y + dy / size }, state.zoom, frame) }))
      return
    }
    const scale = imagePixelsPerScreenPixel(frame, view.zoom, size)
    setBox(current.kind === 'move' ? moveBox(current.box, dx * scale, dy * scale, frame) : resizeBox(current.box, current.handle, dx * scale, dy * scale, frame))
  }

  const onPointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId)
    const current = gesture.current
    if (current === null) return
    if (current.kind === 'pinch' ? pointers.current.size < 2 : current.id === event.pointerId) gesture.current = null
  }

  const nudge = (event: KeyboardEvent<HTMLElement>, apply: (dx: number, dy: number) => CropBox) => {
    const arrow = ARROWS[event.key]
    if (arrow === undefined || busy) return
    event.preventDefault()
    event.stopPropagation()
    const step = event.shiftKey ? 10 : 1
    setBox(apply(arrow[0] * step, arrow[1] * step))
  }

  const { fitW, fitH } = fitOf(frame)
  const wrapStyle: CSSProperties = {
    width: percent(fitW * view.zoom, 1),
    height: percent(fitH * view.zoom, 1),
    left: percent(0.5 - (fitW * view.zoom) / 2 + view.pan.x, 1),
    top: percent(0.5 - (fitH * view.zoom) / 2 + view.pan.y, 1),
  }
  const boxStyle: CSSProperties = {
    left: percent(box.left, frame.width),
    top: percent(box.top, frame.height),
    width: percent(boxWidth(box), frame.width),
    height: percent(boxHeight(box), frame.height),
  }
  const side = resultSide(box, review.margin, review.maxSide)
  const zoomText = `×${(Math.round(view.zoom * 10) / 10).toLocaleString('ru-RU')}`

  return (
    <section className="platform-crop" aria-label="Проверка обрезки фото" data-busy={busy ? 'true' : undefined}>
      <h3 className="platform-crop__title">Проверьте обрезку</h3>
      <p className="platform-crop__hint">
        Пунктирная рамка показывает, где пройдёт обрезка. Если режет лишнее, потяните рамку за угол или сторону; для точности увеличьте фото.
      </p>
      {review.statusText !== null && (
        <p className="platform-crop__note" role="status">
          <Info size={16} aria-hidden="true" /> {review.statusText}
        </p>
      )}

      <div
        ref={stage}
        className="platform-crop__stage"
        role="group"
        aria-label={`Фото без фона ${String(frame.width)} на ${String(frame.height)} точек с рамкой обрезки`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        <div className="platform-crop__canvas" style={wrapStyle}>
          {broken ? (
            <span className="platform-crop__broken" role="alert">
              <ImageOff size={28} aria-hidden="true" /> Фото без фона не открылось. Нажмите «Отмена» и загрузите снимок ещё раз.
            </span>
          ) : (
            <img
              className="platform-crop__image"
              src={review.cutoutUrl}
              alt="Фото без фона"
              draggable={false}
              onError={() => setBroken(true)}
            />
          )}
          <div
            className="platform-crop__box"
            data-role="box"
            style={boxStyle}
            tabIndex={0}
            role="group"
            aria-label={`Рамка обрезки ${String(boxWidth(box))} на ${String(boxHeight(box))} точек. Стрелки двигают рамку`}
            onKeyDown={(event) => nudge(event, (dx, dy) => moveBox(box, dx, dy, frame))}
          >
            {HANDLE_ORDER.map((handle) => (
              <span
                key={handle}
                className="platform-crop__handle"
                data-handle={handle}
                style={HANDLE_AT[handle]}
                tabIndex={0}
                role="button"
                aria-label={`${HANDLE_LABELS[handle]}. Стрелки двигают границу`}
                onKeyDown={(event) => nudge(event, (dx, dy) => resizeBox(box, handle, dx, dy, frame))}
              />
            ))}
          </div>
        </div>
      </div>

      {busy && busyText !== null && (
        <p className="platform-crop__note" role="status">
          <Info size={16} aria-hidden="true" /> {busyText}
        </p>
      )}

      <p className="platform-crop__size">
        В рамке {String(boxWidth(box))} × {String(boxHeight(box))} px. Получится квадрат {String(side)} × {String(side)} px
        {willShrink(box, review.margin, review.maxSide) ? ' (кадр уменьшится до предела; предмет не растягивается)' : ' (с белым полем и отступом, предмет не растягивается)'}.
      </p>

      <div className="platform-crop__row" role="group" aria-label="Увеличение фото">
        <Button
          icon={<Minus size={16} aria-hidden="true" />}
          disabled={busy || view.zoom <= ZOOM_MIN}
          disabledReason={view.zoom <= ZOOM_MIN ? 'Фото уже показано целиком.' : null}
          onClick={() => zoomBy(1 / ZOOM_STEP)}
          actionId="platform.photo-crop.zoom-out"
        >
          Уменьшить
        </Button>
        <Button
          icon={<Plus size={16} aria-hidden="true" />}
          disabled={busy || view.zoom >= ZOOM_MAX}
          disabledReason={view.zoom >= ZOOM_MAX ? 'Больше увеличивать нельзя.' : null}
          onClick={() => zoomBy(ZOOM_STEP)}
          actionId="platform.photo-crop.zoom-in"
        >
          Увеличить
        </Button>
        <Button
          icon={<Scan size={16} aria-hidden="true" />}
          disabled={busy || (view.zoom === ZOOM_MIN && view.pan.x === 0 && view.pan.y === 0)}
          disabledReason="Фото уже показано целиком."
          onClick={() => setView(HOME)}
          actionId="platform.photo-crop.zoom-fit"
        >
          По размеру
        </Button>
        <span className="platform-crop__zoom" aria-label={`Увеличение ${zoomText}`}>
          {zoomText}
        </span>
      </div>

      <div className="platform-crop__row">
        <Button
          icon={<RotateCcw size={16} aria-hidden="true" />}
          disabled={busy || sameBox(box, proposed)}
          disabledReason="Рамка и так стоит там, где её предложил сервер."
          onClick={() => setBox(proposed)}
          actionId="platform.photo-crop.reset"
        >
          Как предложено
        </Button>
      </div>

      <div className="platform-crop__row platform-crop__row--end">
        <Button variant="primary" icon={<Check size={16} aria-hidden="true" />} busy={busy} disabled={broken} onClick={() => onAccept(box)} actionId={acceptKind === 'save' ? 'platform.photo-crop.accept' : 'platform.photo-registry.add-preview'}>
          {acceptKind === 'save' ? 'Принять' : 'Показать результат'}
        </Button>
        <Button variant="ghost" icon={<X size={16} aria-hidden="true" />} onClick={onCancel} actionId="platform.photo-crop.cancel">
          Отмена
        </Button>
      </div>
    </section>
  )
}
